import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import SkSelect from './SkSelect';
import SkDatePicker from './SkDatePicker';
import '../assets/css/filterPanel.css';

const FilterPanel = ({ filterList, setFilterList, onClose, onClearAll, columns, data = [] }) => {

  // --- CONFIGURATION: Define dropdown options ---
  const COLUMN_OPTIONS = {
    'Status': ['In Progress', 'Complete', 'On Hold', 'Not Started', 'Not Possible', 'Testing'],
    'Priority': ['High', 'Medium', 'Low'],
    'isManuallyRaised': ['Yes', 'No'],
    'Manual': ['Yes', 'No'],
    'IsSupportTeam': ['Yes', 'No'],
    'Support': ['Yes', 'No'],
    'QueryType': ['BUG', 'New Task', 'Rework'],
    // ---> ADDED IS UPDATE OPTIONS HERE <---
    'IsUpdate': ['Backend work', 'Update', 'Not update', 'None'],
    /* The grid spells this IsUpdate and the printed report spells it isUpdate;
       both reach here, and a lookup that missed would quietly downgrade the
       Value box to free text on one screen and not the other. Matched on the
       label too, for the same reason. */
    'isUpdate': ['Backend work', 'Update', 'Not update', 'None'],
    'Is Update': ['Backend work', 'Update', 'Not update', 'None'],
  };

  const getOptionsForColumn = (colKey, colLabel) => {
    return COLUMN_OPTIONS[colKey] || COLUMN_OPTIONS[colLabel] || null;
  };

  const isDateColumn = (col) => col?.type === 'date' || col?.type === 'datetime';

  // --- Type-to-search suggestions for plain text columns ---------------
  // A column with no fixed option list still gets a searchable dropdown, built
  // from the values actually present in the grid. Only the columns currently
  // in use are scanned, and only when the chosen column changes (not on every
  // keystroke), so this stays cheap on large reports.
  const activeColumnKeys = filterList.map((f) => f.column).join('|');

  const valueSuggestions = useMemo(() => {
    const map = {};

    filterList.forEach((f) => {
      const col = columns.find((c) => c.key === f.column);
      if (!col || map[col.key]) return;
      if (isDateColumn(col) || getOptionsForColumn(col.key, col.label)) return;

      const seen = new Set();
      for (const row of data) {
        let raw = col.searchValue ? col.searchValue(row) : row[col.key];
        if (typeof raw === 'boolean') raw = raw ? 'Yes' : 'No';
        if (raw === null || raw === undefined) continue;

        const text = String(raw).trim();
        if (!text || text === '-') continue;

        seen.add(text);
        if (seen.size >= 500) break; // menu is scrollable, but stay sane
      }

      map[col.key] = Array.from(seen).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
      );
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns, activeColumnKeys]);

  const addFilterRow = () => {
    const firstCol = columns[0];
    const isDate = firstCol?.type === 'date' || firstCol?.type === 'datetime';
    setFilterList([
      ...filterList,
      { id: Date.now(), column: firstCol.key, operator: isDate ? 'between' : 'contains', value: '', startDate: '', endDate: '' }
    ]);
  };

  const removeFilterRow = (id) => {
    setFilterList(filterList.filter(f => f.id !== id));
  };

  const updateFilterRow = (id, field, val) => {
    setFilterList(filterList.map(f => {
      if (f.id === id) {
        const updated = { ...f, [field]: val };
        
        if (field === 'column') {
          const colDef = columns.find(c => c.key === val);
          const isDate = colDef?.type === 'date' || colDef?.type === 'datetime';
          const hasOptions = getOptionsForColumn(colDef.key, colDef.label);

          if (isDate) {
            updated.operator = 'between';
          } else if (hasOptions) {
            updated.operator = 'equals';
            updated.value = hasOptions[0];
          } else {
            updated.operator = 'contains';
            updated.value = '';
          }
          updated.startDate = '';
          updated.endDate = '';
        }
        return updated;
      }
      return f;
    }));
  };

  // 2. Define the Modal Content
  const modalContent = (
    <>
      {/* 1. BACKDROP (Extreme Z-Index to cover sidebar) */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Slightly darker for better focus
          zIndex: 100000, // Very high to cover sidebar (usually 1000-2000)
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* 2. FILTER PANEL (Centered Modal) */}
      <div 
        className="d-flex flex-column bg-white shadow-lg" 
        style={{ 
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)', 
          zIndex: 100001, // Must be higher than backdrop
          borderRadius: '12px',
          width: '95vw',
          maxWidth: '900px',
          maxHeight: '85vh',
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light rounded-top">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-funnel-fill text-primary"></i>
            <span className="fw-bold text-dark">Advanced Filters</span>
          </div>
          <button className="btn btn-sm btn-close" onClick={onClose}></button>
        </div>

        {/* BODY */}
        <div className="p-3" style={{ overflowY: 'auto' }}>
          {filterList.length === 0 ? (
            <div className="text-center py-5 border rounded-3 bg-light text-muted border-dashed">
              <i className="bi bi-filter-circle display-6 mb-3 d-block opacity-25"></i>
              <p className="mb-3">No filters applied.</p>
              <button className="btn btn-outline-primary btn-sm" onClick={addFilterRow}>
                <i className="bi bi-plus-lg me-1"></i> Add First Condition
              </button>
            </div>
          ) : (
            filterList.map((filter, index) => {
              const colDef = columns.find(c => c.key === filter.column);
              const isDate = colDef?.type === 'date' || colDef?.type === 'datetime';
              const dropdownOptions = getOptionsForColumn(colDef?.key, colDef?.label);
              const suggestions = valueSuggestions[filter.column] || [];

              return (
                <div key={filter.id} className="sk-filter-row">

                  {/* Column Select */}
                  <div className="sk-filter-field sk-filter-field--column">
                    <label className="sk-filter-label">Column</label>
                    <SkSelect
                      width="100%"
                      value={filter.column}
                      options={columns.map(col => ({ value: col.key, label: col.label }))}
                      onChange={(value) => updateFilterRow(filter.id, 'column', value)}
                      ariaLabel="Filter column"
                      searchable
                      placeholder="Select column"
                      searchPlaceholder="Type to search column…"
                    />
                  </div>

                  {/* Operator Select */}
                  <div className="sk-filter-field sk-filter-field--condition">
                    <label className="sk-filter-label">Condition</label>
                    <SkSelect
                      width="100%"
                      value={filter.operator}
                      options={
                        isDate
                          ? [
                              { value: 'between', label: 'Range' },
                              { value: 'equals', label: 'Exact Date' },
                            ]
                          : dropdownOptions
                            ? [
                                { value: 'equals', label: 'Is' },
                                { value: 'notEquals', label: 'Is Not' },
                              ]
                            : [
                                { value: 'contains', label: 'Contains' },
                                { value: 'equals', label: 'Equals' },
                                { value: 'notEquals', label: 'Not Equals' },
                                { value: 'startsWith', label: 'Starts With' },
                              ]
                      }
                      onChange={(value) => updateFilterRow(filter.id, 'operator', value)}
                      ariaLabel="Filter condition"
                    />
                  </div>

                  {/* Value Input */}
                  <div className="sk-filter-field sk-filter-field--value">
                    <label className="sk-filter-label">Value</label>
                    {isDate ? (
                      <div className="sk-filter-daterange">
                        <SkDatePicker
                          block
                          value={filter.startDate}
                          onChange={(value) => updateFilterRow(filter.id, 'startDate', value)}
                          ariaLabel="From date"
                        />
                        {filter.operator === 'between' && (
                          <>
                            <span className="sk-filter-dash">-</span>
                            <SkDatePicker
                              block
                              value={filter.endDate}
                              minDate={filter.startDate}
                              onChange={(value) => updateFilterRow(filter.id, 'endDate', value)}
                              ariaLabel="To date"
                            />
                          </>
                        )}
                      </div>
                    ) : dropdownOptions ? (
                      <SkSelect
                        width="100%"
                        value={filter.value}
                        options={dropdownOptions.map((opt) => ({ value: opt, label: opt }))}
                        onChange={(value) => updateFilterRow(filter.id, 'value', value)}
                        ariaLabel="Filter value"
                        searchable
                        placeholder="Select value"
                        searchPlaceholder="Type to search…"
                      />
                    ) : suggestions.length > 0 ? (
                      // Free-text column: type to search the values actually
                      // present in the grid, or just type your own.
                      <SkSelect
                        width="100%"
                        value={filter.value}
                        options={suggestions.map((opt) => ({ value: opt, label: opt }))}
                        onChange={(value) => updateFilterRow(filter.id, 'value', value)}
                        ariaLabel="Filter value"
                        searchable
                        allowFreeText
                        placeholder="Type value..."
                        searchPlaceholder="Type to search…"
                      />
                    ) : (
                      <input
                        type="text"
                        className="sk-filter-value-input"
                        placeholder="Type value..."
                        value={filter.value}
                        onChange={(e) => updateFilterRow(filter.id, 'value', e.target.value)}
                      />
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    className="sk-filter-remove"
                    onClick={() => removeFilterRow(filter.id)}
                    title="Remove condition"
                  >
                    <i className="bi bi-trash3-fill"></i>
                  </button>

                </div>
              );
            })
          )}
        </div>

        {/* FOOTER */}
        <div className="p-3 border-top bg-light rounded-bottom d-flex justify-content-between align-items-center">
          <button className="btn btn-outline-primary btn-sm fw-bold" onClick={addFilterRow}>
            <i className="bi bi-plus-lg me-1"></i> Add Condition
          </button>
          <div className="d-flex gap-2">
            <button className="btn btn-light text-danger btn-sm border" onClick={onClearAll}>Clear All</button>
            <button className="btn btn-primary btn-sm px-4 fw-bold" onClick={onClose}>Apply Filters</button>
          </div>
        </div>

      </div>
    </>
  );

  // 3. Render into the Body (React Portal)
  return ReactDOM.createPortal(modalContent, document.body);
};

export default FilterPanel;